import React, { useCallback, useState, useEffect, CSSProperties, useRef } from 'react';
import { Dropzone } from './Dropzone';
import { useDispatch } from 'react-redux';
import { ApplicationDispatch, SET_SERVER_MESSAGE } from '../store/application/types';
import List from '@mui/material/List';
import { ApplicationDocument } from './ApplicationDocument';

import {
  useQueryApplicationDocumentFile,
  useApplicationDocumentTypes,
  Document,
  deleteApplicationDocument,
  uploadFile
} from '../Data/ApplicationDocumentData';
import Viewer from 'react-viewer';
import { queryCache } from 'react-query';
import { isSupportedDocument } from '../utils/files';
import { RequestDocuments } from './Shared/RequestDocuments';
import { LinearProgress } from '@mui/material';
import { Splitter, SplitterOnChangeEvent, SplitterPaneProps } from '@progress/kendo-react-layout';
import { LoadingPanel } from '@amsanalytics/react-shared-library';

export interface ApplicationDocumentsProps {
  applicationId: string;
  documents: Document[];
  style?: CSSProperties;
  selectedIds?: number[];
  onSelectId?: (id: string) => void;
  itemId?: number;
}

const MemoizedViewer = React.memo((props: { selectedDocument?: Document, selectedDocumentObjectUrl?: string, container: any }) => {
  return <Viewer
    key={props.selectedDocument?.name}
    visible={!!props.selectedDocumentObjectUrl}
    images={[{
      src: props.selectedDocumentObjectUrl || '',
      alt: props.selectedDocument?.name,
    }]}
    container={props.container}
    noClose
    noNavbar
    noImgDetails
    changeable={false}
    showTotal={false}
  />
});
export const ApplicationDocuments: React.FC<ApplicationDocumentsProps> = (props) => {
  const dispatch = useDispatch<ApplicationDispatch>();
  const [refresh, setRefresh] = useState({});
  const documentsBeingUploaded = useRef<Document[]>([]);
  const [isUploadingDocuments, setIsUploadingDocuments] = useState(false);
  const [isDownloadingDocument, setIsDownloadingDocument] = useState(false);
  const [itemId, setItemId] = useState<number | string | undefined>();
  const [selectedIds, setSelectedIds] = useState<string[] | number[]>();
  const [panes, setPanes] = useState<SplitterPaneProps[]>([
    { size: '75%', min: '25%', resizable: true },
    { min: '25%', resizable: true },
  ]);
  const applicationDocumentTypes = useApplicationDocumentTypes(dispatch);

  const documents = [...(props.documents || [])];

  const [selectedDocument, setSelectedDocument] = useState<Document | undefined>();

  // automatically selects the first selected document and displays it in the document viewer
  useEffect(() => {
    if (props.itemId && props.selectedIds) {
      if (props.itemId !== itemId || props.selectedIds !== selectedIds) {
        setItemId(props.itemId);
        setSelectedIds(props.selectedIds);
        const initialSelectedDocument = props.documents.find(d => props.selectedIds?.includes(d.id));
        if (initialSelectedDocument) {
          setSelectedDocument(initialSelectedDocument);
        } else {
          if(props.documents?.find(d => d.id === selectedDocument?.id) === undefined){
            setSelectedDocument(undefined);
          }
        }
      }
    } else {
      if(props.documents?.find(d => d.id === selectedDocument?.id) === undefined){
        setSelectedDocument(undefined);
      }
    }
  }, [props.itemId, itemId, props.documents, props.selectedIds]);

  if (documentsBeingUploaded.current.length <= 0 && isUploadingDocuments) {
    setIsUploadingDocuments(false);
  }

  const onDelete = useCallback((id: number) => {
    deleteApplicationDocument(id)
      .then(() => {
        queryCache.refetchQueries(['applicationDocuments', props.applicationId]);
      })
      .catch((err) => {
        dispatch({
          type: SET_SERVER_MESSAGE,
          payload: { isError: true, message: `${err}` }
        });
      });
  }, [documents]);

  const onDropAccepted = useCallback((files: File[]) => {
    setIsUploadingDocuments(true);
    files.forEach(f => {
      const d: Document = {
        id: 0,
        documentId: '',
        name: f.name,
        mimeType: f.type,
        applicationId: props.applicationId,
        applicationDocumentTypeIds: undefined,
        file: f,
        notes: undefined,
        isNew: true,
        allowCustomerToViewInPortal: false,
      };

      uploadFile(props.applicationId, f)
        .then((ad) => {
          d.documentId = ad.documentId;
          d.name = ad.name;
          d.mimeType = ad.mimeType;
          d.applicationId = ad.applicationId;
          d.applicationDocumentTypeIds = ad.applicationDocumentTypeIds;
        })
        .catch((reason: Error) => {
          dispatch({ type: "SET_SERVER_MESSAGE", payload: { message: reason.message, isError: true } });
          d.error = reason.message;
        })
        .finally(() => {
          d.file = undefined;
          const index = documentsBeingUploaded.current.findIndex(x => x === d);
          if (index >= 0) {
            queryCache.refetchQueries(['applicationDocuments', props.applicationId])
              .then(() => {
                documentsBeingUploaded.current = []; // clear out currently-uploaded docs list upon successful refetching
              })
              .finally(() => {
                setRefresh({});
              });
          } else {
            console.error(`Unable to find document for refreshing:\n  ${JSON.stringify(d)}`);
          }
        });

      documentsBeingUploaded.current.push(d);
    });
    // Upload the documents and show progress.
    setRefresh({});
  }, [documents]);

  const { data: selectedDocumentObjectUrl } = useQueryApplicationDocumentFile(dispatch, selectedDocument?.documentId, selectedDocument?.name, selectedDocument?.preSignedUrl);

  // Hide loading spinner when the document has been downloaded or if the download failed.
  useEffect(() => {
    if (selectedDocumentObjectUrl && isDownloadingDocument) {
      setIsDownloadingDocument(false);
    }
  }, [selectedDocumentObjectUrl]);

  const previewPaneRef = useRef<HTMLDivElement>(null);

  return (
    <div style={props.style}>
      <Splitter style={{ height: '100%' }} 
        panes={panes} 
        onChange={(event: SplitterOnChangeEvent) => setPanes(event.newState)}>

        {/* {!isDownloadingDocument && ( */}
        <div ref={previewPaneRef} style={{ height: '100%', position: 'relative' }}>
          {isSupportedDocument(selectedDocument?.mimeType) ?
            <MemoizedViewer
              selectedDocument={selectedDocument}
              selectedDocumentObjectUrl={selectedDocumentObjectUrl}
              container={previewPaneRef?.current || undefined}
            />
            :
            <iframe src={selectedDocumentObjectUrl}
              width="100%"
              height="99%"
              style={{
                maxHeight: "95vh",
              }}
            >
              iframes not supported.
            </iframe>
          }
          {isDownloadingDocument && (
            <LoadingPanel label="Downloading document..." style={{ position: 'absolute', color: 'black', display: 'flex', flex: 1, height: '100%', alignItems: 'center', justifyContent: 'center', zIndex: 3001, background: 'gray', opacity: .8, width: '100%' }} />
          )}
        </div>
        {/* )} */}
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            maxHeight: "95vh",
            height: '100%',
          }}
        >
          <div
            style={{
              flex: "0 0",
            }}
          >
            <Dropzone
              disabled={isUploadingDocuments}
              onDropAccepted={onDropAccepted}
            />
            {
              documentsBeingUploaded.current?.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    flex: '0 0',
                    alignItems: 'baseline',
                    padding: '0 10px',
                    width: '100%',
                  }}
                >
                  <span style={{ marginRight: '5px' }}>Uploading...</span>
                  <LinearProgress color='primary' style={{ width: '100%' }} />
                </div>
              )
            }
          </div>
          <div
            style={{
              overflowY: "auto",
              flex: "1 1 auto",
            }}
          >
            <List dense>
              {applicationDocumentTypes && documents?.map(d =>
                <ApplicationDocument
                  key={`${d.name}-${d.mimeType}`}
                  applicationId={props.applicationId}
                  document={d}
                  applicationDocumentTypes={applicationDocumentTypes}
                  isEditing={(d.isNew && !d.file && !d.error)}
                  isSelected={selectedDocument?.id === d.id}
                  onSelected={() => {
                    // Do not re-download the document if it is already selected.
                    if (selectedDocument?.id === d.id) {
                      return;
                    }
                    setIsDownloadingDocument(true);
                    setSelectedDocument(d);
                  }}
                  onDelete={onDelete}
                  isReadonly={false}
                  isCheckable={!!props.selectedIds}
                  isChecked={props.selectedIds?.includes(d.id)}
                  onChecked={props.onSelectId}
                />
              )}
            </List>
          </div>
          <div style={{
            display: "flex",
            justifyContent: "center",
            padding: '5px'
          }}>
            <RequestDocuments />
          </div>
        </div>
      </Splitter>
    </div>
  );
}
